// vg-binding: P7.D-08
//
// apps/vendor/src/features/products/ProductsTable.tsx
//
// Plain table for the products list page — matches the lightweight
// pattern used by OrdersTable.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { cn, ImageViewerModal } from '@printway/ui';
import { formatCents } from '@printway/shared/currency';

import { useTranslation } from '../../lib/i18n/index.js';
import { STATUS_PILL_CLASS } from '../../lib/ui/statusPill.js';
import { InactiveCountdownChip } from './InactiveCountdownChip.js';
import {
  useCancelInactivate,
  useInactivateProduct,
  useReactivateProduct,
  useResubmitProduct,
  type ProductRow,
  type ProductState,
  type ProductsApiError,
} from './api.js';

const STATE_PILL: Record<ProductState, string> = {
  pending: STATUS_PILL_CLASS.info,
  live: STATUS_PILL_CLASS.success,
  inactive_countdown: STATUS_PILL_CLASS.progress,
  inactive: STATUS_PILL_CLASS.done,
  rejected: STATUS_PILL_CLASS.danger,
};

// Edit affordance disabled for these states
// (no delete — only inactive; must reactivate before editing).
const EDIT_DISABLED_STATES = new Set<ProductState>([
  'inactive',
  'inactive_countdown',
]);

function EditButton({ row }: { row: ProductRow }): JSX.Element {
  const { t } = useTranslation('products');
  const navigate = useNavigate();
  const disabled = EDIT_DISABLED_STATES.has(row.state);
  return (
    <button
      type="button"
      data-testid={`products-edit-${row._id}`}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) navigate(`/products/${row._id}/edit`);
      }}
      title={disabled ? t('table.editDisabledInactive') : undefined}
      className={cn(
        'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
        disabled
          ? 'cursor-not-allowed border-pw-border bg-pw-surface text-pw-text-muted'
          : 'border-pw-border bg-pw-surface text-pw-text hover:bg-pw-bg',
      )}
    >
      {t('table.editButton')}
    </button>
  );
}

interface Props {
  rows: ProductRow[];
  isLoading: boolean;
  onSelect?: (row: ProductRow) => void;
}


function RowActions({ row }: { row: ProductRow }): JSX.Element | null {
  const { t } = useTranslation('products');
  const [error, setError] = useState<string | null>(null);
  const inactivateM = useInactivateProduct();
  const cancelM = useCancelInactivate();
  const reactivateM = useReactivateProduct();
  const resubmitM = useResubmitProduct();

  function onError(err: unknown): void {
    const e = err as ProductsApiError;
    setError(e.userMessage ?? e.message ?? t('table.actionFailed'));
  }

  const baseInput = { product_id: row._id, version: row.version };
  const btn =
    'rounded-md border border-pw-border bg-pw-surface px-2.5 py-1 text-xs font-medium text-pw-text hover:bg-pw-bg disabled:cursor-not-allowed disabled:opacity-60';

  let buttons: JSX.Element | null = null;
  if (row.state === 'live') {
    buttons = (
      <button
        type="button"
        data-testid={`products-inactivate-${row._id}`}
        disabled={inactivateM.isPending}
        onClick={(e) => {
          e.stopPropagation();
          setError(null);
          inactivateM.mutate(baseInput, { onError });
        }}
        className={btn}
      >
        {t('table.actionInactivate')}
      </button>
    );
  } else if (row.state === 'inactive_countdown') {
    buttons = (
      <button
        type="button"
        data-testid={`products-cancel-countdown-${row._id}`}
        disabled={cancelM.isPending}
        onClick={(e) => {
          e.stopPropagation();
          setError(null);
          cancelM.mutate(baseInput, { onError });
        }}
        className={btn}
      >
        {t('table.actionCancelCountdown')}
      </button>
    );
  } else if (row.state === 'inactive') {
    buttons = (
      <button
        type="button"
        data-testid={`products-reactivate-${row._id}`}
        disabled={reactivateM.isPending}
        onClick={(e) => {
          e.stopPropagation();
          setError(null);
          reactivateM.mutate(baseInput, { onError });
        }}
        className={btn}
      >
        {t('table.actionReactivate')}
      </button>
    );
  } else if (row.state === 'rejected') {
    buttons = (
      <button
        type="button"
        data-testid={`products-resubmit-${row._id}`}
        disabled={resubmitM.isPending}
        onClick={(e) => {
          e.stopPropagation();
          setError(null);
          resubmitM.mutate(baseInput, { onError });
        }}
        className={btn}
      >
        {t('table.actionResubmit')}
      </button>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      {buttons}
      {error ? <span className="text-[10px] text-red-600">{error}</span> : null}
    </div>
  );
}

export function ProductsTable({ rows, isLoading, onSelect }: Props): JSX.Element {
  const { t } = useTranslation('products');
  // R3-7 (2026-05-16) — clicking a thumbnail opens the shared image viewer.
  const [viewer, setViewer] = useState<{ images: string[]; alt: string } | null>(
    null,
  );

  if (isLoading && rows.length === 0) {
    return (
      <div className="flex flex-col gap-2 py-6">
        {Array.from({ length: 6 }).map((_v, i) => (
          <div
            key={i}
            className="h-12 animate-pulse rounded-md border border-pw-border bg-pw-surface"
          />
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-8 text-center text-sm text-pw-text-muted">
        {t('table.emptyTab')}
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-auto text-sm">
        <thead className="border-b border-pw-border text-xs uppercase tracking-wider text-pw-text-muted">
          <tr>
            <th className="px-3 py-2 text-left">{t('table.headerImage')}</th>
            <th className="px-3 py-2 text-left">{t('table.headerName')}</th>
            <th className="px-3 py-2 text-left">{t('table.headerSkuPrefix')}</th>
            <th className="px-3 py-2 text-left">{t('table.headerType')}</th>
            <th className="px-3 py-2 text-left">{t('table.headerStatus')}</th>
            <th className="px-3 py-2 text-right">{t('table.headerCostPrice')}</th>
            {/* Amendment #4 — capacity is per-variant now; dropped
                from the product-level list. */}
            <th className="px-3 py-2 text-left">{t('table.headerVersion')}</th>
            <th className="px-3 py-2 text-center">{t('table.headerEdit')}</th>
            <th className="px-3 py-2 text-right">{t('table.headerActions')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r._id}
              data-testid={`products-row-${r._id}`}
              onClick={() => onSelect?.(r)}
              className={cn(
                'border-b border-pw-border transition-colors',
                onSelect ? 'cursor-pointer hover:bg-pw-surface' : '',
              )}
            >
              <td className="px-3 py-2">
                {r.cover_image_url ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewer({ images: [r.cover_image_url!], alt: r.name });
                    }}
                    aria-label={t('table.imageAlt', { name: r.name })}
                    className="block h-12 w-12 overflow-hidden rounded-md ring-1 ring-pw-border hover:ring-pw-primary"
                  >
                    <img
                      src={r.cover_image_url}
                      alt=""
                      loading="lazy"
                      className="h-12 w-12 object-cover"
                    />
                  </button>
                ) : (
                  <div
                    aria-hidden="true"
                    className="flex h-12 w-12 items-center justify-center rounded-md bg-pw-surface text-[10px] uppercase text-pw-text-muted ring-1 ring-pw-border"
                  >
                    n/a
                  </div>
                )}
              </td>
              <td className="px-3 py-2 font-medium text-pw-text">{r.name}</td>
              <td className="px-3 py-2 font-mono text-xs">{r.sku_prefix}</td>
              <td className="px-3 py-2 text-pw-text-muted">{r.product_type}</td>
              <td className="px-3 py-2">
                <span className={cn('inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold', STATE_PILL[r.state])}>
                  {r.state}
                </span>
                {r.state === 'inactive_countdown' ? (
                  <span className="ml-2">
                    <InactiveCountdownChip ends_at={r.inactive_countdown_ends_at} />
                  </span>
                ) : null}
              </td>
              <td className="px-3 py-2 text-right" data-testid={`products-row-cost-${r._id}`}>
                {/* D17a — SKUs priced differently: show the lowest as "from $min". */}
                {typeof r.cost_min_cents === 'number' &&
                typeof r.cost_max_cents === 'number' &&
                r.cost_min_cents !== r.cost_max_cents
                  ? t('table.costFrom', { price: formatCents(r.cost_min_cents) })
                  : formatCents(r.cost_min_cents ?? r.cost_price_cents)}
              </td>
              <td className="px-3 py-2 font-mono text-xs text-pw-text-muted">v{r.version}</td>
              <td className="px-3 py-2 text-center">
                <EditButton row={r} />
              </td>
              <td className="px-3 py-2 text-right">
                <RowActions row={r} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ImageViewerModal
        open={viewer !== null}
        images={viewer?.images ?? []}
        alt={viewer?.alt ?? ''}
        onClose={() => setViewer(null)}
      />
    </div>
  );
}

export default ProductsTable;
